import { asMutableType, } from '@tymia/shared';
import { supportedLocales, } from '../translation/translation.js';
import { locationType, salutation, userInsuranceType, } from '../user/UserApi.js';
import { nullableEnum, } from '../validator.js';
import { appointmentTypeLocation, } from './AppointmentTypeApi.js';
export const appointmentModuleUrl = import.meta.url;
export const appointmentState = asMutableType(Object.freeze([
    'available',
    'taken',
    'billable',
    'bill',
]));
export const appointmentSeriesInterval = asMutableType(Object.freeze([
    'P1W',
    'P2W',
    'P3W',
    'P4W',
]));
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function appointmentSaveRequestSchema() {
    return {
        type: 'object',
        additionalProperties: false,
        properties: {
            id: {
                type: ['number', 'null'],
            },
            therapistId: {
                type: 'integer',
            },
            customerId: {
                type: 'integer',
            },
            appointmentStart: {
                type: 'string',
                format: 'date-time',
            },
            appointmentEnd: {
                type: 'string',
                format: 'date-time',
            },
            typeId: {
                type: 'integer',
            },
            bookability: {
                type: 'string',
                enum: ['public', 'internal'],
            },
            sendEmails: {
                type: 'boolean',
            },
            // location is nullable as long as we can get
            // the value from appointment type
            // 'both' is allowed as long no client
            location: nullableEnum(appointmentTypeLocation),
            notes: {
                type: ['string', 'null'],
            },
            maxParticipants: {
                type: 'integer',
                minimum: 1,
                maximum: 50,
            },
            series: {
                type: 'object',
                additionalProperties: false,
                properties: {
                    interval: {
                        type: 'string',
                        enum: appointmentSeriesInterval,
                    },
                    count: {
                        type: 'number',
                        minimum: 1,
                        maximum: 50,
                    },
                },
                required: [
                    'interval',
                    'count',
                ],
            },
            clientRemoveAction: {
                type: 'string',
                enum: ['cancel', 'delete'],
            },
            clients: {
                type: 'array',
                maxItems: 50,
                uniqueItems: true,
                items: {
                    type: 'object',
                    additionalProperties: false,
                    properties: {
                        id: {
                            type: ['integer', 'null'],
                        },
                        salutation: nullableEnum(salutation),
                        title: {
                            type: ['string', 'null'],
                        },
                        firstName: {
                            type: 'string',
                        },
                        lastName: {
                            type: 'string',
                        },
                        email: {
                            type: 'string',
                            format: 'idn-email',
                        },
                        phoneCountry: {
                            type: ['string', 'null'],
                        },
                        phone: {
                            type: ['string', 'null'],
                        },
                        insuranceType: nullableEnum(userInsuranceType),
                        street: {
                            // always requred if used by client
                            type: ['string', 'null'],
                        },
                        postalCode: {
                            // always requred if used by client
                            type: ['string', 'null'],
                        },
                        city: {
                            // always requred if used by client
                            type: ['string', 'null'],
                        },
                    },
                    required: [
                        'firstName',
                        'lastName',
                        'email',
                    ],
                },
            },
            medicalFeeSchedules: {
                type: 'array',
                maxItems: 50,
                items: {
                    oneOf: [
                        {
                            type: 'object',
                            additionalProperties: false,
                            properties: {
                                id: {
                                    type: 'number',
                                },
                                codeValue: {
                                    type: 'string',
                                },
                                alternativeTitle: {
                                    type: ['string', 'null'],
                                    nullable: true,
                                },
                                incrementalRate: {
                                    type: 'string',
                                },
                                coachingTitle: {
                                    type: 'null',
                                },
                                coachingAmount: {
                                    type: 'null',
                                },
                                coachingAmountTax: {
                                    type: 'null',
                                },
                            },
                            required: [
                                'codeValue',
                                'incrementalRate',
                            ],
                        },
                        {
                            type: 'object',
                            additionalProperties: false,
                            properties: {
                                id: {
                                    type: 'number',
                                },
                                codeValue: {
                                    type: 'null',
                                },
                                alternativeTitle: {
                                    type: 'null',
                                },
                                incrementalRate: {
                                    type: 'null',
                                },
                                coachingTitle: {
                                    type: 'string',
                                },
                                coachingAmount: {
                                    type: 'string',
                                },
                                coachingAmountTax: {
                                    type: 'string',
                                    enum: ['notax', 'add'],
                                },
                            },
                            required: [
                                'coachingTitle',
                                'coachingAmount',
                                'coachingAmountTax',
                            ],
                        },
                    ],
                },
            },
        },
        required: [
            'therapistId',
            'customerId',
            'appointmentStart',
            'appointmentEnd',
            'typeId',
            'bookability',
            'sendEmails',
            'location',
            'notes',
            'clients',
            'medicalFeeSchedules',
        ],
    };
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function appointmentSaveResponseSchema() {
    return {
        type: 'object',
        additionalProperties: false,
        properties: {
            id: {
                type: 'integer',
            },
            seriesId: {
                type: ['string', 'null'],
            },
        },
        required: [
            'id',
        ],
    };
}
export function isMedicalFeeScheduleLine(item) {
    return 'incrementalRate' in item;
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function appointmentBookRequestSchema() {
    return {
        type: 'object',
        additionalProperties: false,
        properties: {
            locale: {
                type: 'string',
                enum: supportedLocales(),
            },
            insuranceType: {
                type: 'string',
                enum: userInsuranceType,
            },
            typeId: {
                type: 'string',
                format: 'decimal',
            },
            location: {
                type: 'string',
                enum: locationType,
            },
            salutation: {
                type: 'string',
                enum: salutation,
            },
            firstName: {
                type: 'string',
                minLength: 1,
            },
            lastName: {
                type: 'string',
                minLength: 1,
            },
            email: {
                type: 'string',
                format: 'idn-email',
            },
            phoneCountry: {
                type: 'string',
                minLength: 1,
            },
            phone: {
                type: 'string',
                minLength: 1,
                pattern: '^[0-9]+$',
            },
            street: {
                type: 'string',
                minLength: 1,
            },
            postalCode: {
                type: 'string',
                minLength: 1,
            },
            city: {
                type: 'string',
                minLength: 1,
            },
            privacyConsent: {
                const: true,
            },
        },
        required: [
            'locale',
            'insuranceType',
            'typeId',
            'location',
            'salutation',
            'firstName',
            'lastName',
            'email',
            'phoneCountry',
            'phone',
            'street',
            'postalCode',
            'city',
            'privacyConsent',
        ],
    };
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function appointmentBookResponseSchema() {
    return {
        type: 'object',
        additionalProperties: false,
        properties: {
            status: {
                const: 'ok',
            },
            appointmentStart: {
                type: 'string',
                format: 'date-time',
            },
        },
        required: [
            'status',
            'appointmentStart',
        ],
    };
}
export async function appointmentBook(baseUrl, { appointmentId, ...props }) {
    const result = await fetch(`${baseUrl}/api/appointment/book/${appointmentId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(props),
    });
    if (result.ok) {
        const data = await result.json();
        return data;
    }
    return {
        status: 'network error',
        httpStatus: result.status,
        message: await result.text(),
    };
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function searchBookableAppointmentsRequestSchema() {
    return {
        type: 'object',
        additionalProperties: false,
        properties: {
            therapistId: {
                type: 'string',
                format: 'decimal',
            },
            startDate: {
                type: 'string',
                format: 'date-time',
            },
            endDate: {
                type: ['string', 'null'],
                format: 'date-time',
                nullable: true,
            },
            insuranceType: {
                type: 'string',
                enum: userInsuranceType,
            },
            meetingTypeId: {
                type: 'string',
                format: 'decimal',
            },
        },
        required: [
            'therapistId',
            'startDate',
        ],
    };
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function searchBookableAppointmentsResponseSchema() {
    return {
        type: 'object',
        additionalProperties: false,
        properties: {
            status: {
                const: 'ok',
            },
            appointments: {
                type: 'array',
                items: {
                    type: 'object',
                    additionalProperties: false,
                    properties: {
                        id: {
                            type: 'string',
                            format: 'decimal',
                        },
                        appointmentStart: {
                            type: 'string',
                            format: 'date-time',
                        },
                    },
                    required: [
                        'id',
                        'appointmentStart',
                    ],
                },
            },
            types: {
                type: 'array',
                items: {
                    type: 'object',
                    additionalProperties: false,
                    properties: {
                        id: {
                            type: 'string',
                            format: 'decimal',
                        },
                        name: {
                            type: 'string',
                        },
                        insuranceType: nullableEnum(userInsuranceType),
                        location: {
                            type: 'string',
                            enum: appointmentTypeLocation,
                        },
                        duration: {
                            type: 'string',
                            format: 'decimal',
                        },
                    },
                    required: [
                        'id',
                        'name',
                        'insuranceType',
                        'location',
                        'duration',
                    ],
                },
            },
            typesFiltered: {
                type: 'array',
                items: {
                    type: 'string',
                    format: 'decimal',
                },
            },
            prevCursor: {
                type: ['string', 'null'],
                nullable: true,
                format: 'date-time',
            },
            nextCursor: {
                type: ['string', 'null'],
                nullable: true,
                format: 'date-time',
            },
        },
        required: [
            'status',
            'appointments',
            'types',
            'typesFiltered',
            'prevCursor',
            'nextCursor',
        ],
    };
}
export async function searchBookableAppointments(baseUrl, props) {
    const result = await fetch(`${baseUrl}/api/appointment/bookable`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(props),
    });
    if (result.ok) {
        const data = await result.json();
        return data;
    }
    return {
        status: 'network error',
        httpStatus: result.status,
        message: await result.text(),
    };
}
//# sourceMappingURL=AppointmentApi.js.map